import React, { useState } from 'react';
import { User } from 'firebase/auth';
import { setGuardianMatchOverride } from '../../api/caseDocuments';
import {
  Guardian,
  GuardianMatchEntry,
  GuardianMatchOverride,
  GUARDIAN_MATCH_LABEL,
  MinorCaseFields,
  OVERRIDE_VALUES,
  SOLE_GUARDIAN_REASON_LABEL,
  documentKindLabel,
  formatTimestamp,
  fullName,
} from './minorCase';

interface CaseDocument {
  id: string;
  fileName: string;
  fileType: string;
  [key: string]: any;
}

interface ApplicantGuardiansProps {
  caseId: string;
  caseDetails: MinorCaseFields & {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    birthdate?: string;
    nationality?: string;
  };
  documents: CaseDocument[];
  user: User | null;
  onOpenDocument: (fileName: string) => void;
  onOverrideSaved: (override: GuardianMatchOverride | null) => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode; testId?: string }> = ({
  label,
  children,
  testId,
}) => (
  <div className="minor-field">
    <span className="info-item-label">{label}</span>
    <span className="info-item-value" data-testid={testId}>{children}</span>
  </div>
);

const SignatureStatus: React.FC<{ guardian: Guardian }> = ({ guardian }) => {
  if (guardian.signature_status === 'signed') {
    const when = formatTimestamp(guardian.signed_at);
    return (
      <span className="minor-pill minor-pill--ok">
        Signed{when ? ` ${when}` : ''}
      </span>
    );
  }
  return <span className="minor-pill minor-pill--pending">Not signed yet</span>;
};

const MatchLine: React.FC<{ label: string; entry?: GuardianMatchEntry | null }> = ({ label, entry }) => {
  if (!entry) return null;
  const matched = entry.matched === true;
  return (
    <li className="guardian-match-line">
      <span
        className={`minor-pill ${matched ? 'minor-pill--ok' : 'minor-pill--review'}`}
        aria-label={matched ? 'matched' : 'not matched'}
      >
        {matched ? 'Matches' : 'No match'}
      </span>
      <span>
        <strong>{label}</strong>
        {entry.name ? `: ${entry.name}` : ''}
        {entry.note ? ` (${entry.note})` : ''}
      </span>
    </li>
  );
};

/**
 * Who the NIF is for and who signs for them, on a minor's case.
 *
 * The child's details come off the child's document (the case's own name
 * fields, as on an adult case); the guardians, the guardian check and the
 * sole-guardian evidence are minor-only fields written by user-backend. The
 * only thing the lawyer writes here is the override of the guardian check, in
 * the same edit/save/cancel pattern as the Finanças reference.
 */
const ApplicantGuardians: React.FC<ApplicantGuardiansProps> = ({
  caseId,
  caseDetails,
  documents,
  user,
  onOpenDocument,
  onOverrideSaved,
}) => {
  const [editing, setEditing] = useState(false);
  const [valueDraft, setValueDraft] = useState(OVERRIDE_VALUES[0].value);
  const [reasonDraft, setReasonDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const guardians = [...(caseDetails.guardians ?? [])].sort(
    (a, b) => Number(a.index) - Number(b.index)
  );
  const sole = caseDetails.sole_guardian;
  const override = caseDetails.guardian_match_override ?? null;
  const status = caseDetails.guardian_match_status ?? null;
  const details = caseDetails.guardian_match_details ?? null;

  const evidence =
    documents.find(
      d => sole?.evidence_document_id
        && (d.id === sole.evidence_document_id || d.fileName === sole.evidence_document_id)
    ) ?? documents.find(d => d.fileType === 'sole_guardian_evidence');

  const startEditing = () => {
    setValueDraft(override?.value || OVERRIDE_VALUES[0].value);
    setReasonDraft(override?.reason ?? '');
    setError('');
    setEditing(true);
  };

  const save = async (value: string, reason: string) => {
    if (!user) return;
    setSaving(true);
    setError('');
    try {
      const token = await user.getIdToken();
      const result = await setGuardianMatchOverride(token, caseId, value, reason);
      const stored = result.guardian_match_override;
      onOverrideSaved(
        stored
          ? {
              ...stored,
              by: stored.by ?? null,
              // The backend stamps the server time; until the case reloads,
              // now is close enough to show.
              at: result.changed ? new Date().toISOString() : override?.at,
            }
          : null
      );
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the guardian check override.');
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = () => {
    if (!window.confirm('Remove your override? The case goes back to what the automatic check found.')) return;
    save('', '');
  };

  const childName = fullName(caseDetails) || caseDetails.birth_certificate?.child_name || 'Not known yet';

  return (
    <section className="case-info-card applicant-guardians" aria-labelledby="applicant-guardians-title">
      <h2 id="applicant-guardians-title" className="applicant-guardians-title">
        Applicant and guardians
      </h2>

      <div className="applicant-guardians-grid">
        <div className="minor-person" data-testid="minor-child">
          <h3>Child (applicant)</h3>
          <Field label="Name">{childName}</Field>
          <Field label="Date of birth">
            {caseDetails.birthdate || caseDetails.birth_certificate?.child_birthdate || 'Not known yet'}
          </Field>
          <Field label="Nationality">{caseDetails.nationality || 'Not known yet'}</Field>
          <Field label="Identified by">{documentKindLabel(caseDetails.applicant?.document_kind)}</Field>
        </div>

        {guardians.map(guardian => (
          <div key={guardian.index} className="minor-person" data-testid={`minor-guardian-${guardian.index}`}>
            <h3>Parent {guardian.index}</h3>
            <Field label="Name">{fullName(guardian) || 'Not known yet'}</Field>
            <Field label="Document">
              {documentKindLabel(guardian.document_kind)}
              {guardian.document_number ? ` ${guardian.document_number}` : ''}
              {guardian.expiry_date ? `, valid until ${guardian.expiry_date}` : ''}
            </Field>
            <Field label="Email">{guardian.email || 'Not given yet'}</Field>
            <Field label="Signature" testId={`minor-guardian-${guardian.index}-signature`}>
              <SignatureStatus guardian={guardian} />
            </Field>
          </div>
        ))}

        {sole && (
          <div className="minor-person" data-testid="minor-sole-guardian">
            <h3>Sole guardian</h3>
            <Field label="Why one parent signs">
              {(sole.reason && SOLE_GUARDIAN_REASON_LABEL[sole.reason]) || sole.reason || 'Not given'}
            </Field>
            <Field label="Evidence">
              {evidence ? (
                <button
                  type="button"
                  className="financas-btn financas-btn--link"
                  onClick={() => onOpenDocument(evidence.fileName)}
                >
                  View proof of sole guardianship
                </button>
              ) : (
                'Not uploaded yet'
              )}
            </Field>
          </div>
        )}
      </div>

      <div className="guardian-match" data-testid="guardian-match">
        <div className="guardian-match-header">
          <span className="info-item-label">Guardian check</span>
          <span
            className={`minor-pill ${
              status === 'verified' ? 'minor-pill--ok' : status === 'review' ? 'minor-pill--review' : 'minor-pill--pending'
            }`}
            data-testid="guardian-match-status"
          >
            {(status && GUARDIAN_MATCH_LABEL[status]) || 'Not run yet'}
          </span>
          {override && (
            <span className="minor-pill minor-pill--ok" data-testid="guardian-match-override">
              Overridden: {GUARDIAN_MATCH_LABEL[override.value] || override.value}
            </span>
          )}
        </div>
        {details && (
          <ul className="guardian-match-lines">
            <MatchLine label="Child" entry={details.child} />
            <MatchLine label="Parent 1" entry={details.guardian_1} />
            <MatchLine label="Parent 2" entry={details.guardian_2} />
          </ul>
        )}

        <div className="guardian-match-override">
          <span className="info-item-label">Your override</span>
          {editing ? (
            <div className="guardian-match-override-editor">
              <select
                className="status-select"
                aria-label="Override value"
                value={valueDraft}
                onChange={event => setValueDraft(event.target.value)}
                disabled={saving}
              >
                {OVERRIDE_VALUES.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <input
                type="text"
                className="financas-reference-input guardian-match-reason"
                aria-label="Reason for the override"
                placeholder="Why, e.g. mother's maiden name on the certificate"
                maxLength={500}
                value={reasonDraft}
                onChange={event => setReasonDraft(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' && reasonDraft.trim()) save(valueDraft, reasonDraft);
                  if (event.key === 'Escape') setEditing(false);
                }}
                disabled={saving}
                autoFocus
              />
              <button
                type="button"
                className="financas-btn financas-btn--link"
                onClick={() => save(valueDraft, reasonDraft)}
                disabled={saving || !reasonDraft.trim()}
              >
                {saving ? 'Saving…' : 'Save override'}
              </button>
              <button
                type="button"
                className="financas-btn financas-btn--link"
                onClick={() => { setEditing(false); setError(''); }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="guardian-match-override-view">
              <span className="info-item-value" data-testid="guardian-match-override-text">
                {override
                  ? `${GUARDIAN_MATCH_LABEL[override.value] || override.value}: ${override.reason}`
                  : 'None'}
              </span>
              {override && (override.by || override.at) && (
                <span className="guardian-match-override-meta">
                  {[override.by, formatTimestamp(override.at)].filter(Boolean).join(', ')}
                </span>
              )}
              <span className="guardian-match-override-actions">
                <button type="button" className="financas-btn financas-btn--link" onClick={startEditing}>
                  {override ? 'Change override' : 'Override check'}
                </button>
                {override && (
                  <button
                    type="button"
                    className="financas-btn financas-btn--link"
                    onClick={clearOverride}
                    disabled={saving}
                  >
                    Remove override
                  </button>
                )}
              </span>
            </div>
          )}
          {error && <span className="financas-office-error" role="alert">{error}</span>}
        </div>
      </div>
    </section>
  );
};

export default ApplicantGuardians;
